import React, { useState } from 'react';

interface IMarketOption {
    id: number;
    name: string;
    selected: boolean;
    code: string;
    active: boolean;
}

type MarketsSelectorProps = {
  options: IMarketOption[];
  onChange: (next: IMarketOption[], action?: 'toggle' | 'select-all' | 'select-none') => void;
  error?: React.ReactNode;
  label?: string;
  required?: boolean;
  defaultOpen?: boolean;
  testId?: string;
};

export default function MarketsSelector({
  options,
  onChange,
  error,
  label = 'Markets',
  required = false,
  defaultOpen = false,
  testId,
}: MarketsSelectorProps) {
  const [open, setOpen] = useState(defaultOpen);
  const markets = options || [];
  const selected = markets.filter(option => option.selected);
  const allSelected = markets.length > 0 && selected.length === markets.length;
  const countLabel = allSelected ? `All ${String(label).toLowerCase()} selected` : `${selected.length} selected`;

  const sanitize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

   const toggleMarket = (market, index) => {
        const copyMarkets = [...markets];
        copyMarkets[index] = { ...market, selected: !market.selected };
        onChange(copyMarkets, 'toggle');
  };

  const handleChipKeyDown = (event, market, index) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggleMarket(market, index);
  };

    const selectAll = () => {
      const next = markets.map(m => ({ ...m, selected: true}));
      onChange(next, 'select-all');
    };
    const selectNone = () => {
      const next = markets.map(m => ({ ...m, selected: false }));
      onChange(next, 'select-none');
    };

  return (
    <div className="field full-span" data-testid={testId || undefined}>
      <div className="collapse-header">
        <div>
          <label className="label">
            {label} {required && <span className="required-dot" aria-label="required">*</span>}
          </label>
          <div className="muted" style={{ marginTop: 2 }}>{countLabel}</div>
        </div>
        <button
          type="button"
          className="subtle-btn"
          onClick={() => setOpen((v) => !v)}
          aria-label={`${open ? 'Hide' : 'Edit'} ${label}`}
          aria-expanded={open}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {open ? 'Hide' : 'Edit'}
        </button>
      </div>
      {open && (
        <>
          {markets.length > 0 && (
            <div className="chip-actions">
              <button type="button" className="subtle-btn" onClick={selectAll} data-testid={testId ? `${testId}-select-all` : undefined}>Select All</button>
              <button type="button" className="subtle-btn" onClick={selectNone} data-testid={testId ? `${testId}-select-none` : undefined}>Select None</button>
            </div>
          )}
          <div className={`chip-group ${error ? 'error' : ''}`} data-testid={testId ? `${testId}-chips` : undefined}>
            {markets.length === 0 ? (
              <div className="muted">No {label} Available</div>
            ) : markets.map((m, index) => {
              const active = m.selected; 
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`chip ${active ? 'active' : ''}`}
                  onClick={() => toggleMarket(m, index)}
                  onKeyDown={(event) => handleChipKeyDown(event, m, index)}
                  aria-label={`${m.name}, ${active ? 'selected' : 'not selected'}`}
                  aria-pressed={active}
                  data-testid={testId ? `${testId}-chip-${sanitize(m.id)}` : undefined}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </>
      )}
      {error && <div className="error-text" data-testid={testId ? `${testId}-error` : undefined}>{error}</div>}
    </div>
  );
}
